import React from 'react';
import { motion } from 'framer-motion';
import { rembursment } from '../../utils/Helpers';

const MemberDetailsModal = ({ isOpen, onClose, memberDetails, error }) => {
  if (!isOpen) return null;

  const handleOutsideClick = (e) => {
    if (e.target.id === "modal-overlay") {
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      id="modal-overlay"
      onClick={handleOutsideClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.95, y: -20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: -20 }}
        transition={{ duration: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 overflow-hidden"
      >
        <div className="w-full">
          {error ? (
            <div className="text-center py-8">
              <div className="text-red-500 dark:text-red-400 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-base text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          ) : !memberDetails ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-500 dark:text-gray-400">Loading details...</p>
            </div>
          ) : (
            <>
              {/* Header with Member Name */}
              <div className=" bg-[#62ace8] dark:bg-[#02183a] p-6 text-white">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">{memberDetails.name || 'Member Details'}</h2>
                    <p className="text-blue-100 mt-1">Reimbursement Information</p>
                    {(memberDetails.CNIC || memberDetails.cnic) && (
                      <p className="text-white/90 mt-2 text-sm font-mono tracking-wide">
                        CNIC: {memberDetails.CNIC || memberDetails.cnic}
                      </p>
                      
                    )}
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Type-wise Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {Object.entries(memberDetails.MedicalDetails).map(([type, details], index) => (
                    <motion.div
                      key={type}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow"
                    >
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                        {rembursment[type.replace('Type', '')]}
                      </h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-300">Total Limit</span>
                          <span className="font-semibold text-gray-800 dark:text-white">Rs. {details.totalLimit.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-300">Used Amount</span>
                          <span className="font-semibold text-blue-600 dark:text-blue-400">Rs. {details.usedAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 dark:text-gray-300">Remaining</span>
                          <span className="font-semibold text-green-600 dark:text-green-400">Rs. {details.remaining.toLocaleString()}</span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Overall Summary */}
                <div className="bg-gradient-to-r bg-[#62ace8] dark:bg-[#02183a] rounded-lg p-6 shadow-lg">
                  <h3 className="text-xl font-bold text-white mb-6">Overall Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <p className="text-white/90 mb-1">Total Limit</p>
                      <p className="text-2xl font-bold text-white">
                        Rs. {memberDetails.totals.totalLimit.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <p className="text-white/90 mb-1">Total Used</p>
                      <p className="text-2xl font-bold text-white">
                        Rs. {memberDetails.totals.totalUsed.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <p className="text-white/90 mb-1">Total Remaining</p>
                      <p className="text-2xl font-bold text-white">
                        Rs. {memberDetails.totals.totalRemaining.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MemberDetailsModal; 